<template>
  <span class="inline-name-editor">
    <template v-if="!editing">
      <span class="inline-name-editor-view">
        <span
          class="name-text"
          :class="{ 'is-clickable': canEdit }"
          :title="$t('global.edit')"
          @click="onNameClick"
          @keydown.enter.prevent="onNameClick"
          @keydown.space.prevent="onNameClick"
          :tabindex="canEdit ? 0 : -1"
          role="button"
        >
          {{ value || placeholder }}
        </span>
        <b-button
          v-if="canEdit"
          size="is-small"
          type="is-text"
          icon-left="pencil"
          class="edit-btn"
          :title="$t('global.edit')"
          @click.stop.prevent="beginEdit"
          :aria-label="$t('global.edit')"
        />
      </span>
      <CopyToClipboard :value="value || ''" v-if="showCopy" class="copy-btn"/>
    </template>
    <template v-else>
      <input
        type="text"
        v-model="draft"
        :placeholder="placeholder"
        class="inline-editor-input name-text"
        @keydown.enter.prevent="saveEdit"
        @keydown.esc.prevent="cancelEdit"
        @blur="cancelEdit"
        ref="inputRef"
        :aria-invalid="!!error"
        :aria-label="placeholder || value"
      />
    </template>
  </span>
</template>

<script lang="ts">
import { defineComponent, ref, watch, nextTick, onMounted } from 'vue'
import CopyToClipboard from '@/components/CopyToClipboard.vue'

export default defineComponent({
  name: 'InlineNameEditor',
  components: { CopyToClipboard },
  props: {
    modelValue: { type: String, default: '' },
    placeholder: { type: String, default: '' },
    canEdit: { type: Boolean, default: true },
    showCopy: { type: Boolean, default: false },
    validate: { type: Function as unknown as () => ((name: string) => string | null | Promise<string | null>), default: undefined },
    save: { type: Function as unknown as () => ((name: string) => Promise<void>), default: undefined },
  },
  emits: ['update:modelValue', 'saved', 'editing-change'],
  setup(props, { emit }) {
    const value = ref<string>(props.modelValue || '')
    const draft = ref<string>(value.value)
    const editing = ref<boolean>(false)
    const saving = ref<boolean>(false)
    const error = ref<string>('')
    const inputRef = ref<any>(null)

    const isDirty = ref<boolean>(false)

    const focusInput = async () => {
      await nextTick()
      const el = (inputRef.value && (inputRef.value.$el || inputRef.value.focus && inputRef.value)) as any
      if (el && el.focus) el.focus()
    }

    const beginEdit = async () => {
      if (!props.canEdit) return
      editing.value = true
      error.value = ''
      draft.value = value.value
      emit('editing-change', true)
      await focusInput()
    }

    const onNameClick = () => {
      if (!props.canEdit) return
      void beginEdit()
    }

    const cancelEdit = () => {
      editing.value = false
      error.value = ''
      draft.value = value.value
      emit('editing-change', false)
    }

    const runValidation = async (name: string): Promise<string | null> => {
      if (!props.validate) return null
      try {
        const res = await (props.validate as any)(name)
        return res
      } catch (e: any) {
        return String(e?.message || e)
      }
    }

    const saveEdit = async () => {
      const name = (draft.value || '').trim()
      if (!name) {
        error.value = (window as any).$t ? (window as any).$t('validations.required') : 'Input required'
        return
      }
      const validationError = await runValidation(name)
      if (validationError) {
        error.value = validationError
        return
      }
      if (!props.save) {
        value.value = name
        emit('update:modelValue', value.value)
        editing.value = false
        emit('editing-change', false)
        emit('saved', value.value)
        return
      }
      try {
        saving.value = true
        await (props.save as any)(name)
        value.value = name
        emit('update:modelValue', value.value)
        editing.value = false
        emit('editing-change', false)
        emit('saved', value.value)
      } catch (e: any) {
        error.value = String(e?.message || e)
      } finally {
        saving.value = false
      }
    }

    watch(() => props.modelValue, (nv) => {
      value.value = nv || ''
      if (!editing.value) draft.value = value.value
      isDirty.value = (draft.value || '').trim() !== (value.value || '').trim()
    }, { immediate: true })

    watch(() => draft.value, (nv) => {
      isDirty.value = (nv || '').trim() !== (value.value || '').trim()
    })

    onMounted(() => {
      // noop
    })

    return { CopyToClipboard, value, draft, editing, saving, error, inputRef, beginEdit, cancelEdit, saveEdit, isDirty, onNameClick }
  }
})
</script>

<style scoped>
.inline-name-editor {
  display: inline-flex;
  align-items: center;
  .inline-name-editor-view {
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }
}
.name-text.is-clickable {
  cursor: text;
}
.inline-name-editor:hover .name-text.is-clickable {
  border-bottom-color: currentColor;
}
.inline-name-editor-view .edit-btn {
  transition: opacity 0.2s ease;
  opacity: 0.5;
}
.inline-name-editor-view:hover .edit-btn {
  opacity: 1;
}
.inline-editor-input {
  border: none;
  background: transparent;
  padding: 0;
  margin: 0;
  height: auto;
  line-height: inherit;
  font: inherit;
  font-weight: inherit;
  color: inherit;
}
.inline-editor-input:focus {
  outline: none;
}
</style>
